/**
 * Type for filter values that can be converted to query string parameters
 */
type FilterValue = string | number | boolean | null | undefined;

/**
 * Type for a record of filters
 */
type FilterRecord = Record<string, FilterValue>;

/**
 * Removes null, undefined, and empty string values from the filters object
 *
 * @param filters - The filters object to clean
 * @returns A new object with only non-empty values
 */
export function cleanFilters(
  filters: FilterRecord
): Record<string, string | number | boolean> {
  const cleaned: Record<string, string | number | boolean> = {};

  Object.entries(filters).forEach(([key, value]) => {
    // Only include values that are not null, undefined, or empty strings
    if (value !== null && value !== undefined && value !== '') {
      cleaned[key] = value;
    }
  });

  return cleaned;
}

/**
 * Converts a filters object to a URL query string
 *
 * @param filters - The filters object to convert
 * @returns A URL-encoded query string (without the leading '?')
 *
 * @example
 * ```typescript
 * const filters = { page: 1, size: 10, status: 'active', search: '' };
 * const queryString = toQueryString(filters);
 * // Returns: "page=1&size=10&status=active"
 * ```
 */
export function toQueryString(filters: FilterRecord): string {
  const cleaned = cleanFilters(filters);
  const params = new URLSearchParams();

  Object.entries(cleaned).forEach(([key, value]) => {
    params.append(key, String(value));
  });

  return params.toString();
}
